import React, { useState, useContext } from 'react';
import { qs } from 'utils';
import PropTypes from 'prop-types';
import { Link } from '@reach/router';
import { I18N, I18NConsumer, GlobalContext } from 'commons';
import {
  Navbar,
  NavbarBrand,
  NavbarToggler,
  Collapse,
  Toggle,
  Nav as BSNav,
  NavItem,
  NavLink,
  Button,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { orgId as organizationId, baseUrl, memberApp, cookies, isCommsDetail } from 'utils';
import HelpAndAnswerTip from './HelpAndAnswerTip';

const EmptyNavItem = styled(NavItem)`
  padding: 16.5px 0;
`;

const Nav = ({ path }) => {
  const [open, setOpen] = useState(false);
  const accessKey = qs?.accessKey || '';
  const param = (accessKey === '') ? '' : `?accessKey=${accessKey}`;
  const {
    account: { id: accountId, name: accountName },
    navConfig: {
      showLogin, showSignUp, showCrisisTask, showCrisisSingleEventDashboard, showOnCallSchedule,
      showNotification, showWeather, showEvent, showIncidents,
      showOverview, showFAQs, showDirectoryChat, showPublicFilesLibrary, showTravel
    },
    commsData: {
      orgId: orgIdFromComms
    },
    universalProfileUrl,
    setIsPublicPortalEnabled,
    isPublicPortalEnabled,
    flags: { PUBLIC_PORTAL_FORCE_NEW_ACCOUNT_TO_UP }
  } = useContext(GlobalContext);
  const { pathname } = window.location;

  const entryFromPublicPortal = cookies.get("entryFromPublicPortal", {
    path: `/${organizationId}`
  });


  const navItems = (() => {
    const orgId = (isCommsDetail && orgIdFromComms) ? orgIdFromComms : organizationId;
    const navItemList = [];
    const params = window.location.search;
    const jspPageUrl = `${baseUrl}/${accountId ? `manage/${orgId}/${accountId}` : `index/${orgId}`
    }`;
    if (showOnCallSchedule) {
      navItemList.push({
        key: 0,
        text: 'button.myschedule',
        desc: 'button.myschedule',
        href: `${jspPageUrl}#/scheduling`,
      });
    }
    if (accountId) {
      if (!entryFromPublicPortal) {
        navItemList.push({
          key: 1,
          text: 'button.home',
          desc: 'button.home',
          href: `${jspPageUrl}`,
        });
      } else {
        navItemList.push({
          key: 1,
          text: 'button.home',
          desc: 'button.home',
          href: `${baseUrl}/${orgId}/portal-home`,
        });
      }
    } else if (!memberApp) {
      if (showLogin && !entryFromPublicPortal) {
        navItemList.push({
          key: 2,
          text: 'ssp.login.login',
          desc: 'ssp.login.login',
          href: `/${orgId}/login${param}`,
          activeName: 'login'
        });
      }

      if (showSignUp && !entryFromPublicPortal) {
        const signupHref = PUBLIC_PORTAL_FORCE_NEW_ACCOUNT_TO_UP && universalProfileUrl
          ? `${universalProfileUrl}?orgId=${orgId}`
          : `/${orgId}/new`;

        navItemList.push({
          key: 3,
          text: 'ssp.register.title',
          desc: 'ssp.register.title',
          href: signupHref,
          'eb-test': 'ee_nav_button_signup',
          activeName: 'new_12',
        });
      }
    }

    if (sessionStorage.getItem("isPublicPortalEnabled") === "true") {
      setIsPublicPortalEnabled(true)
    }

    if (entryFromPublicPortal) {
      navItemList.push({
        key: 10,
        text: 'nav.back.to.portal',
        desc: 'nav.back.to.portal',
        href: `${universalProfileUrl}/home`,
        'eb-test': 'ee_nav_button_portal',
      });
    }
    if (showCrisisTask || showCrisisSingleEventDashboard) {
      navItemList.push({
        key: 4,
        text: 'criticalevents.menu.title.cm',
        desc: 'criticalevents.menu.discription.cm',
        'eb-test': 'ee_nav_button_cm',
        href: `/${orgId}/cm`,
        activeName: 'cm',
      });
    }

    if (showNotification || showWeather) {
      if (showNotification) {
        navItemList.push({
          key: 5,
          text: 'notification.title.text',
          desc: 'notification.title.text',
          href: `/${orgId}/notif`,
          activeName: 'notif',
          style: {
            display: /^\/\d+\/(weather|alert)(\/.*)?$/.test(pathname) ? 'none' : '',
          },
        });
      }

      if (showWeather) {
        navItemList.push({
          key: 6,
          text: 'notification.title.text',
          desc: 'notification.title.text',
          href: `/${orgId}/weather${param}`,
          activeName: 'weather',
          style: {
            display:
              (!showNotification && /^\/\d+\/(alert)(\/.*)?$/.test(pathname)) ||
                (showNotification && !/^\/\d+\/weather(\/.*)?$/.test(pathname))
                ? 'none'
                : '',
          },
        });
      }
    }

    if (showIncidents) {
      navItemList.push({
        key: 8,
        text: 'Incidents',
        desc: 'Incidents',
        href: `${baseUrl}/${orgId}/incidents${param}`,
        activeName: 'incidents',
        reloadPage: !accountId
      });
    }

    if (showDirectoryChat) {
      navItemList.push({
        key: 13,
        text: 'secure.chat.title',
        desc: 'secure.chat.title',
        href: `/${orgId}/chat`,
        activeName: 'chat',
      });
    }

    if (showEvent && !memberApp) {
      navItemList.push({
        key: 9,
        text: 'eventSubscription.title.events',
        desc: 'eventSubscription.title',
        'eb-test': 'ee_nav_button_events',
        href: `/${orgId}/event`,
      });
    }

    if (showTravel) {
      navItemList.push({
        key: 15,
        text: 'ssp.title.locations.travel',
        desc: 'ssp.title.locations.travel',
        href: `/${orgId}/travel`,
        activeName: 'travel',
      });
    }

    if (showOverview) {
      navItemList.push({
        key: 10,
        text: 'ssp.main.label.overview',
        desc: 'ssp.main.label.overview',
        href: `/${orgId}/ov${param}`,
      });
    }

    if (showFAQs) {
      navItemList.push({
        key: 11,
        text: 'ssp.main.lable.faqs',
        desc: 'ssp.main.lable.faqs',
        href: `/${orgId}/faq${param}`,
      });
    }
    const newRegisterBar = [];
    if (pathname.includes('/new/account/edit-')) {
      newRegisterBar.push({
        key: 12,
        text: 'ssp.information.review',
        desc: 'ssp.information.review',
        href: `${baseUrl}/${orgId}/new/account/review`,
      });
    }

    if (showPublicFilesLibrary) {
      navItemList.push({
        key: 14, // Use a unique key that doesn't conflict with existing ones
        text: 'files.library.title.text',
        desc: 'files.library.title.text',
        href: `/${orgId}/files`,
        activeName: 'files',
        'eb-test': 'ee_nav_button_files', // Adding this for tracking if needed
      });
    }


    if (/^\/\d+\/new\/login$/.test(pathname)) {
      return navItemList;
    }
    if (/^\/\d+\/new\/.+$/.test(pathname)) {
      return newRegisterBar;
    }
    if (memberApp && /\/cm\/dashboard/i.test(pathname)) {
      return [];
    }
    return navItemList;
  })();

  const logout = e => {
    const orgId = (isCommsDetail && orgIdFromComms) ? orgIdFromComms : organizationId;
    e.preventDefault();

    cookies.remove('orgId');
    cookies.remove('username');
    cookies.remove('password');
    cookies.remove('key');
    cookies.remove('accessKey');
    window.location.href = `${baseUrl}/logout/${orgId}`;
  };

  return (
    <div className="nav-box">
      <Navbar color="dark" dark expand="md">
        {(() => {
          if (!accountId) {
            return <NavbarBrand />;
          }
          return (
            <NavbarBrand tag="span">
              <span className="navbar-welcome">
                <I18N>ssp.main.label.welcome</I18N>
                  &nbsp;
                {
                  !entryFromPublicPortal &&
                  <span title={accountName} className="notranslate">{accountName}</span>
                }
              </span>
              {
                !entryFromPublicPortal &&
                <Button color="link" onClick={logout} eb-test="ee_global_button_logout">
                  <I18N>ssp.main.label.logout</I18N>
                </Button>
              }
            </NavbarBrand>
          );
        })()}
        {
          navItems.length !== 0 &&
          <NavbarToggler onClick={() => setOpen(!open)} aria-label="Toggle menu">
            <FontAwesomeIcon
              icon={faBars}
              width="1.275em"
              height="1.3em"
            />
          </NavbarToggler>
        }
        <Collapse isOpen={open} navbar>
          <BSNav className="ml-auto" navbar>
            {!navItems.length && <EmptyNavItem><br /></EmptyNavItem>}
            {navItems.map(item => (
              <NavItem
                key={item.key}
                style={item.style}
                className={path.startsWith(item.activeName) ? 'active' : ''}
              >
                <I18NConsumer>
                  {(i18n) => ((item.href.startsWith('http') || item.reloadPage || isCommsDetail)
                    ? (
                      <NavLink
                        eb-test={item['eb-test']}
                        title={i18n.get(item.text || item.desc)}
                        href={item.href}
                      >
                        <I18N>{item.text}</I18N>
                      </NavLink>
                    ) : (
                      <NavLink
                        eb-test={item['eb-test']}
                        title={i18n.get(item.text || item.desc)}
                        tag={({ ...props }) => (
                          <Link to={item.href} {...props}>
                            <I18N>{item.text}</I18N>
                          </Link>
                        )}
                      />
                    ))}
                </I18NConsumer>
              </NavItem>
            ))}
          </BSNav>
        </Collapse>
      </Navbar>
      <HelpAndAnswerTip path={path} />
    </div>
  );
};

Nav.propTypes = {
  path: PropTypes.string,
};

Nav.defaultProps = {
  path: '',
};

export default Nav;
